import { useEffect } from "react";

import {
  NetworkStatus,
  useLazyQuery,
  WatchQueryFetchPolicy,
} from "@apollo/client";

import { GetServicesQuery, GetServicesQueryVariables } from "../../../../API";
import { useCustomerIdGuard } from "../../../hooks/useCustomerIdGuard";
import { GET_SERVICES } from "../../../operations/queries";
import { DEFAULT_LIMIT_COUNT } from "../../../variables/common";
import { errorNotification } from "../../../variables/notification";

export const useGetServicesByCustomer = (
  policy: WatchQueryFetchPolicy = "cache-first"
) => {
  const selectedCustomerId = useCustomerIdGuard();

  const [getServices, { data, loading, fetchMore, networkStatus }] =
    useLazyQuery<GetServicesQuery, GetServicesQueryVariables>(GET_SERVICES, {
      fetchPolicy: policy,
      notifyOnNetworkStatusChange: true,
    });

  useEffect((): void => {
    if (selectedCustomerId) {
      getServices({
        variables: {
          limit: DEFAULT_LIMIT_COUNT,
          customerId: selectedCustomerId,
        },
      });
    }
  }, [selectedCustomerId, getServices]);

  useEffect(() => {
    if (!data?.getServices?.nextToken) return;

    const fetchAllPages = async () => {
      let currentNextToken = data.getServices.nextToken;

      try {
        while (currentNextToken) {
          const response = await fetchMore({
            variables: {
              limit: DEFAULT_LIMIT_COUNT,
              customerId: selectedCustomerId,
              nextToken: currentNextToken,
            },
          });

          currentNextToken = response.data?.getServices?.nextToken || "";
        }
      } catch (error) {
        console.error("Error fetching additional service pages:", error);

        errorNotification();
      }
    };

    fetchAllPages();
  }, [data?.getServices?.nextToken, selectedCustomerId, fetchMore]);

  const servicesLoading = loading || networkStatus === NetworkStatus.fetchMore;

  return {
    data,
    loading: servicesLoading,
  };
};
